import type { SceneManager } from '../core/SceneManager';
import type { ScrollController } from '../core/ScrollController';
import { rooms, nodeById, roomById, guidedTourStops as stops } from '../mansion/MansionLayout';
import { floorAtEyeHeight, roomAtPosition } from './FloorManager';
import { FloorPlan } from './FloorPlan';

export class InteriorTour {
  private readonly dialog = document.createElement('dialog');
  private readonly map: FloorPlan;
  private origin?: HTMLElement;
  private host?: HTMLElement;
  private restoreFocus?: HTMLElement;
  private canvasTabIndex: string | null = null;
  private canvasLabel: string | null = null;
  private lastRoom = '';
  private lastStop = 0;
  private open = false;
  constructor(
    private readonly scene: SceneManager,
    private readonly scroll: ScrollController,
  ) {
    this.dialog.className = 'interior-tour';
    this.dialog.setAttribute('aria-labelledby', 'tour-title');
    this.dialog.innerHTML = `<div class="tour-viewport" data-tour-viewport></div>
      <header class="tour-header"><div><span class="tour-eyebrow">BUILT BY KEEMO / THE ESTATE</span><h2 id="tour-title">Inside the mansion</h2></div><button type="button" data-tour-exit>Exit tour <span aria-hidden="true">×</span></button></header>
      <aside class="tour-map"><button type="button" data-map-toggle aria-expanded="${innerWidth > 600}" aria-controls="tour-map-body"><span data-tour-floor-label>Ground floor</span><span>Floor plan <span aria-hidden="true">⌖</span></span></button><div id="tour-map-body" ${innerWidth <= 600 ? 'hidden' : ''}><div data-floor-plan></div><p>Your position · Select a room to visit</p></div></aside>
      <footer class="tour-footer"><div class="tour-room-copy"><span data-tour-status role="status" aria-live="polite">Arriving at the entrance</span><p data-tour-description></p></div>
      <div class="tour-navigation"><button type="button" data-tour-prev aria-label="Previous room">←</button><label><span class="sr-only">Choose a room</span><select data-tour-room aria-label="Choose a room">${[
        'ground',
        'upper',
      ]
        .map(
          (f) =>
            `<optgroup label="${f === 'ground' ? 'Ground floor' : 'Upper floor'}">${rooms
              .filter((r) => r.floor === f)
              .map((r) => `<option value="${r.node}">${r.label}</option>`)
              .join('')}</optgroup>`,
        )
        .join(
          '',
        )}</select></label><button type="button" data-tour-next aria-label="Next room">→</button></div>
      <p class="tour-hint">Drag to look · Choose a room to move · Esc to exit</p></footer>`;
    document.body.append(this.dialog);
    this.map = new FloorPlan(this.dialog.querySelector('[data-floor-plan]')!, (id) =>
      this.scene.tour.navigate(id),
    );
    this.dialog.querySelector('[data-tour-exit]')!.addEventListener('click', () => this.scene.tour.exit());
    this.dialog.querySelector('[data-map-toggle]')!.addEventListener('click', () => {
      const b = this.dialog.querySelector('[data-map-toggle]')!,
        body = this.dialog.querySelector<HTMLElement>('#tour-map-body')!;
      body.hidden = !body.hidden;
      b.setAttribute('aria-expanded', String(!body.hidden));
    });
    this.dialog
      .querySelector('select')!
      .addEventListener('change', (e) => this.scene.tour.navigate((e.target as HTMLSelectElement).value));
    this.dialog.querySelector('[data-tour-next]')!.addEventListener('click', () => this.next(1));
    this.dialog.querySelector('[data-tour-prev]')!.addEventListener('click', () => this.next(-1));
    this.dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.scene.tour.exit();
    });
    this.dialog.addEventListener('keydown', (e) => {
      if ((e.target as Element).matches('select,input,button,[data-map-room]')) return;
      if (['ArrowUp', 'w', 'W', 'ArrowDown', 's', 'S', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight')
          this.scene.tour.look(e.key === 'ArrowLeft' ? -90 : 90, 0);
        else this.scene.tour.step(e.key === 'ArrowDown' || e.key.toLowerCase() === 's');
      }
    });
    scene.onTourFrame = () => this.update();
  }
  enter(): void {
    if (this.open) return;
    const host = document.querySelector<HTMLElement>('[data-scene-host]');
    if (!host || !this.scene.enterTour()) return;
    const explode = document.querySelector('[data-explode]');
    explode?.setAttribute('aria-pressed', 'false');
    if (explode) explode.innerHTML = 'Explode layers <span aria-hidden="true">↕</span>';
    this.restoreFocus = document.activeElement as HTMLElement;
    this.canvasTabIndex = this.scene.renderer.domElement.getAttribute('tabindex');
    this.canvasLabel = this.scene.renderer.domElement.getAttribute('aria-label');
    this.origin = host.parentElement!;
    this.host = host;
    this.open = true;
    this.lastRoom = '';
    this.scroll.stop();
    document.body.classList.add('tour-open');
    this.dialog.showModal();
    this.dialog.querySelector('[data-tour-viewport]')!.append(host);
    this.scene.refreshSize();
    this.scene.renderer.domElement.tabIndex = 0;
    this.scene.renderer.domElement.setAttribute(
      'aria-label',
      'Mansion interior. Drag to look; arrow keys move or turn.',
    );
    this.scene.renderer.domElement.focus();
  }
  private next(direction: number): void {
    const canonical = roomById(nodeById(this.scene.tour.destination).room).node;
    const found = stops.indexOf(canonical),
      index = found < 0 ? this.lastStop : found;
    this.scene.tour.navigate(stops[(index + direction + stops.length) % stops.length]);
  }
  private update(): void {
    if (!this.open) return;
    const tour = this.scene.tour;
    if (!tour.active) {
      this.close();
      return;
    }
    this.dialog.dataset.state = tour.state;
    this.map.update(this.scene.camera);
    const floor = floorAtEyeHeight(this.scene.camera.position.y);
    this.dialog.querySelector('[data-tour-floor-label]')!.textContent = floor.label;
    const current =
      roomAtPosition(
        this.scene.camera.position.x,
        this.scene.camera.position.y,
        this.scene.camera.position.z,
      ) ?? roomById(nodeById(tour.currentNode).room);
    const status =
      tour.state === 'entering'
        ? 'Arriving at the entrance'
        : tour.state === 'exiting'
          ? 'Returning to the grounds'
          : tour.moving
            ? 'Walking to ' + roomById(nodeById(tour.destination).room).label
            : current.label;
    const statusElement = this.dialog.querySelector('[data-tour-status]')!;
    if (statusElement.textContent !== status) statusElement.textContent = status;
    if (current.id !== this.lastRoom) {
      this.dialog.querySelector('[data-tour-description]')!.textContent = current.description;
      this.lastRoom = current.id;
    }
    this.dialog
      .querySelectorAll<HTMLButtonElement | HTMLSelectElement>(
        '.tour-navigation button, .tour-navigation select',
      )
      .forEach((e) => (e.disabled = tour.moving));
    this.dialog.querySelector<HTMLElement>('[data-floor-plan]')!.classList.toggle('map-busy', tour.moving);
    if (!tour.moving) {
      this.dialog.querySelector('select')!.value = current.node;
      const index = stops.indexOf(current.node);
      if (index >= 0) this.lastStop = index;
    }
    this.dialog.querySelector<HTMLButtonElement>('[data-tour-exit]')!.disabled = tour.state === 'exiting';
  }
  close(): void {
    if (!this.open) return;
    this.scene.tour.reset();
    this.open = false;
    if (this.host && this.origin) this.origin.append(this.host);
    const canvas = this.scene.renderer.domElement;
    for (const [name, value] of [
      ['tabindex', this.canvasTabIndex],
      ['aria-label', this.canvasLabel],
    ] as const) {
      if (value === null) canvas.removeAttribute(name);
      else canvas.setAttribute(name, value);
    }
    this.dialog.close();
    document.body.classList.remove('tour-open');
    this.scroll.start();
    this.scene.refreshSize();
    this.scroll.restoreStudy();
    this.restoreFocus?.focus({ preventScroll: true });
  }
  destroy(): void {
    this.close();
    this.scene.onTourFrame = undefined;
    this.dialog.remove();
  }
}
